import { useCallback, useEffect, useMemo, useState } from 'react'
import CustomFileSystemProvider from 'devextreme/file_management/custom_provider'
import { FileManager, Permissions, FileManagerTypes, Upload, ContextMenu, Toolbar } from 'devextreme-react/file-manager'
import FileSystemError from 'devextreme/file_management/error'
import { AttachmentsManagerProps } from '@/components/file-manager/AttachmentsManager.types'
import FileSystemItem from 'devextreme/file_management/file_system_item'
import UploadInfo from 'devextreme/file_management/upload_info'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { LoadIndicator } from 'devextreme-react'
import { BlobSasPermissions, DocumentPermissions } from '@/enums'
import { Providers } from '@microsoft/mgt-react'
import { AzureCustomProvider } from '@/components/file-manager/azure-blob-storage/AzureCustomProvider'
import { SingleOrMultiple } from 'devextreme/common'
import { AxiosResponse } from 'axios'
import notify from 'devextreme/ui/notify'
import { dxFileManagerContextMenuItem, dxFileManagerToolbarItem } from 'devextreme/ui/file_manager'

const getAzureFileSystemProvider = (
  endpointUrl: string,
  rootPath: string,
  createDirectoryAction: (rootFolderPath: string, name: string, conflictBerhavior: string) => Promise<AxiosResponse>,
  onRequestExecuted?: ({
    method,
    urlPath,
    queryString,
  }: {
    method: string
    urlPath: string
    queryString: string
  }) => void,
): CustomFileSystemProvider => {
  return new AzureCustomProvider(endpointUrl, rootPath, createDirectoryAction, onRequestExecuted).fileSystemProvider
}

const extractDirectoryName = (path: string | null | undefined) => {
  if (path === null || path === undefined) return ''
  const pathArray = path.split('/')
  const lastIndex = pathArray.length - 1
  return pathArray[lastIndex]
}

const extractParentPath = (path: string | null | undefined) => {
  if (path === null || path === undefined) return ''
  return path.split('/').slice(0, -1).join('/')
}

const allowedFileExtensions: string[] = []

const selectionMode: SingleOrMultiple | undefined = 'single'

const isDirectoryExpr = 'folder'
const dateModifiedExpr = 'lastModifiedDateTime'

const AttachmentsManager = (props: AttachmentsManagerProps) => {
  const {
    folderUrl,
    fileManagerRef,
    preliminarPermissionsAction,
    createDirectoryAction,
    height = 550,
    storeInAzureBlobStorage = false,
    requestUrl: endpointUrl = `drives/${import.meta.env.VITE_QSADMINSITE_DRIVE_ID}`,
    folderUrlName,
  } = props

  const [isLoading, setIsLoading] = useState(true)
  const [permissions, setPermissions] = useState<DocumentPermissions>()
  const [permissionsAzureBlobStorage, setPermissionsAzureBlobStorage] = useState<BlobSasPermissions[]>([])

  const { client } = useQsAdminApiManager()

  const getItems = useCallback(
    async (parentFolder: FileSystemItem) => {
      let currentFolder = folderUrl
      if (parentFolder.path.length > 0) {
        currentFolder = `${folderUrl}/${parentFolder.path}`
      }
      try {
        const items = await Providers.globalProvider.graph.api(`${endpointUrl}/root:/${currentFolder}:/children`).get()
        return items.value
      } catch (error) {
        console.error(`Error reading ${currentFolder}`, error)
        return []
      }
    },
    [endpointUrl, folderUrl],
  )

  const renameItem = useCallback(
    async (item: FileSystemItem, newName: string) => {
      const body = JSON.stringify({
        name: newName,
      })
      return Providers.globalProvider.graph.api(`${endpointUrl}/items/${item.dataItem.id}`).patch(body)
    },
    [endpointUrl],
  )

  const moveItem = useCallback(
    async (item: FileSystemItem, destinationDirectory: FileSystemItem) => {
      const body = JSON.stringify({
        parentReference: { id: destinationDirectory.dataItem.id },
        name: item.name,
      })
      return Providers.globalProvider.graph?.api(`${endpointUrl}/items/${item.dataItem.id}`).patch(body)
    },
    [endpointUrl],
  )

  const deleteItem = useCallback(
    async (item: FileSystemItem) => {
      return Providers.globalProvider.graph?.api(`${endpointUrl}/items/${item.dataItem.id}`).delete()
    },
    [endpointUrl],
  )

  const createDirectory = useCallback(
    async (parentDirectory: FileSystemItem, name: string) => {
      let currentFolder = folderUrl
      if (parentDirectory.path.length > 0) {
        currentFolder = `${folderUrl}/${parentDirectory.path}`
      }
      return createDirectoryAction(currentFolder ?? '', name, 'rename')
    },
    [createDirectoryAction, folderUrl],
  )

  const uploadFileChunk = useCallback(
    async (file: File, uploadInfo: UploadInfo, destinationDirectory: FileSystemItem) => {
      if (file.size === 0) {
        throw new FileSystemError(
          32_767,
          undefined,
          'Uploading folders is not supported. Please select individual files.',
        )
      }

      try {
        if (uploadInfo.chunkIndex === 0) {
          // Controllo ogni volta l'esistenza della cartalla principale
          try {
            await createDirectoryAction(extractParentPath(folderUrl) ?? '', extractDirectoryName(folderUrl), 'replace')
          } catch {
            // Non segnalo niente la creaazione potrebbe essere già avvenuta
          }

          let currentFolder = folderUrl
          if (destinationDirectory.path.length > 0) {
            currentFolder = `${folderUrl}/${destinationDirectory.path}`
          }

          const driveItem = {
            name: file.name,
          }

          const uploadSession = await Providers.globalProvider.graph
            .api(`${endpointUrl}/root:/${currentFolder}/${file.name}:/createUploadSession`)
            .post({
              item: driveItem,
            })

          uploadInfo.customData.uploadUrl = uploadSession.uploadUrl
        }

        return client.put(uploadInfo.customData.uploadUrl, uploadInfo.chunkBlob, {
          headers: {
            'Content-Length': `${uploadInfo.chunkBlob.size}`,
            'Content-Range': `bytes ${uploadInfo.bytesUploaded}-${uploadInfo.bytesUploaded + uploadInfo.chunkBlob.size - 1}/${file.size}`,
          },
        })
      } catch (error) {
        console.error('Upload error:', error)
        throw error
      }
    },
    [client, createDirectoryAction, endpointUrl, folderUrl],
  )

  const generateShareLinkGraph = useCallback(async (item: FileSystemItem) => {
    try {
      // const body = {
      //   type: 'edit', // 'view' for read-only, 'edit' for editable
      //   scope: 'organization', // 'anonymous', 'organization', or 'users'
      // }

      // const response = await Providers.globalProvider.graph
      //   .api(`${endpointUrl}/items/${item.dataItem.id}/createLink`)
      //   .post(body)

      // return response.link.webUrl
      return item.dataItem.webUrl + `?web=1`
    } catch (error) {
      console.error('Error generating share link:', error)
      throw error
    }
  }, [])

  const generateShareLinkAzure = useCallback(async (item: FileSystemItem) => {
    try {
      // Call your API to generate SAS token
      // const response = await client.post(`${import.meta.env.VITE_QSADMINAPI_HOST}/api/generate-sas-token`, {
      //   blobPath: item.path ? `${folderUrl}/${item.path}/${item.name}` : `${folderUrl}/${item.name}`,
      // })

      const filePreview = await client.get(`${endpointUrl}/getfilepreview?path=${folderUrl}/${item.name}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      return `${filePreview.data.file.sasToken}`
    } catch (error) {
      console.error('Error generating Azure share link:', error)
      throw error
    }
  }, [])

  const handleShareLink = useCallback(
    async (component: any) => {
      const selectedItems = component.getSelectedItems()

      if (selectedItems.length === 0) {
        notify('Selezionare un file per generare il link di condivisione', 'warning', 2000)
        return
      }

      const item = selectedItems[0]

      if (item.isDirectory) {
        notify('La condivisione è disponibile solo per i file, non per le cartelle', 'warning', 2000)
        return
      }

      try {
        let shareLink: string

        if (!storeInAzureBlobStorage) {
          shareLink = await generateShareLinkGraph(item)
        } else {
          shareLink = await generateShareLinkAzure(item)
        }

        // Copy to clipboard
        await navigator.clipboard.writeText(shareLink)
        notify('Link di condivisione copiato negli appunti', 'success', 2000)
      } catch (error) {
        notify('Errore durante la generazione del link di condivisione', 'error', 2000)
        console.error(error)
      }
    },
    [generateShareLinkGraph, generateShareLinkAzure, storeInAzureBlobStorage],
  )

  const handleContextMenuItemClick = useCallback(
    (e: FileManagerTypes.ContextMenuItemClickEvent) => {
      if (e.itemData.name === 'shareLink') {
        handleShareLink(e.component)
      }
    },
    [handleShareLink],
  )

  const handleToolbarItemClick = useCallback(
    (e: FileManagerTypes.ToolbarItemClickEvent) => {
      if (e.itemData.name === 'shareLink') {
        handleShareLink(e.component)
      }
    },
    [handleShareLink],
  )

  const onRequestExecuted = useCallback(
    ({ method, urlPath, queryString }: { method: string; urlPath: string; queryString: string }): void => {},
    [],
  )

  const customFileSystemProvider = useMemo(
    () =>
      !storeInAzureBlobStorage
        ? new CustomFileSystemProvider({
            getItems,
            renameItem,
            moveItem,
            deleteItem,
            createDirectory,
            uploadFileChunk,
            isDirectoryExpr,
            dateModifiedExpr,
          })
        : getAzureFileSystemProvider(
            `${import.meta.env.VITE_QSADMINAPI_HOST}/${endpointUrl}/azureblobstorageaccess`,
            folderUrl ?? '',
            createDirectoryAction,
            onRequestExecuted,
          ),
    [
      createDirectory,
      createDirectoryAction,
      deleteItem,
      endpointUrl,
      folderUrl,
      getItems,
      moveItem,
      onRequestExecuted,
      renameItem,
      storeInAzureBlobStorage,
      uploadFileChunk,
    ],
  )

  const rootFolderName = useMemo(() => {
    return folderUrlName ?? extractDirectoryName(folderUrl)
  }, [folderUrl, folderUrlName])

  const contextMenuItems: (dxFileManagerContextMenuItem | FileManagerTypes.FileManagerPredefinedContextMenuItem)[] =
    useMemo(() => {
      return [
        'create',
        'upload',
        'refresh',
        'download',
        'move',
        'copy',
        'rename',
        'delete',
        {
          name: 'shareLink',
          text: 'Share',
          icon: 'link',
          beginGroup: false,
        },
      ]
    }, [])

  const fileSelectionItemsToolbar: (dxFileManagerToolbarItem | FileManagerTypes.FileManagerPredefinedToolbarItem)[] =
    useMemo(() => {
      return [
        'download',
        'separator',
        'move',
        'copy',
        'rename',
        'separator',
        'delete',
        'separator',
        {
          widget: 'dxButton',
          name: 'shareLink',
          location: 'before',
          options: {
            text: 'Share',
            icon: 'link',
            stylingMode: 'text',
            onClick: () => {
              // Will be handled by onToolbarItemClick
            },
          },
        },
        'clearSelection',
      ]
    }, [])

  useEffect(() => {
    let cancelled = false

    const computePermissions = async () => {
      if (!storeInAzureBlobStorage) {
        let objperm: DocumentPermissions = DocumentPermissions.Deny
        if (preliminarPermissionsAction) {
          objperm = (await preliminarPermissionsAction()) as DocumentPermissions
        }
        if (cancelled) return
        setPermissions(objperm)
      } else {
        let objperm: BlobSasPermissions[] = []
        if (props.preliminarPermissionsAction) {
          objperm = (await props.preliminarPermissionsAction()) as BlobSasPermissions[]
        }
        if (cancelled) return
        setPermissionsAzureBlobStorage(objperm)
      }

      if (cancelled) return
      setIsLoading(false)
    }

    computePermissions()
    return () => {
      cancelled = true
    }
  }, [folderUrl, storeInAzureBlobStorage])

  if (isLoading) {
    return <LoadIndicator id="small-indicator" height={20} width={20} />
  }

  if (
    (permissions && permissions !== DocumentPermissions.Deny) ||
    (permissionsAzureBlobStorage && permissionsAzureBlobStorage.length !== 0)
  ) {
    return (
      <>
        <FileManager
          id="file-manager"
          height={height}
          allowedFileExtensions={allowedFileExtensions}
          selectionMode={selectionMode}
          ref={fileManagerRef}
          fileSystemProvider={customFileSystemProvider}
          onSelectedFileOpened={async (e: FileManagerTypes.SelectedFileOpenedEvent) => {
            if (!storeInAzureBlobStorage) window.open(e.file.dataItem.webUrl + `?web=1`, '_blank')
            else {
              const filePreview = await client.get(`${endpointUrl}/getfilepreview?path=${folderUrl}/${e.file.name}`, {
                headers: {
                  'Content-Type': 'application/json',
                },
              })
              window.open(`${filePreview.data.file.sasToken}`, '_blank')
            }
          }}
          onItemDownloading={(e: FileManagerTypes.ItemDownloadingEvent) => {
            if (!storeInAzureBlobStorage) window.open(e.item.dataItem['@microsoft.graph.downloadUrl'], '_blank')
          }}
          onContextMenuItemClick={handleContextMenuItemClick}
          onToolbarItemClick={handleToolbarItemClick}
          rootFolderName={rootFolderName}
        >
          <Permissions
            create={
              permissions === DocumentPermissions.Write ||
              permissionsAzureBlobStorage.find((perm) => perm === BlobSasPermissions.Write) !== undefined
            }
            move={
              permissions === DocumentPermissions.Write ||
              permissionsAzureBlobStorage.find((perm) => perm === BlobSasPermissions.Write) !== undefined
            }
            delete={
              permissions === DocumentPermissions.Write ||
              permissionsAzureBlobStorage.find((perm) => perm === BlobSasPermissions.Write) !== undefined
            }
            rename={
              permissions === DocumentPermissions.Write ||
              permissionsAzureBlobStorage.find((perm) => perm === BlobSasPermissions.Write) !== undefined
            }
            upload={
              permissions === DocumentPermissions.Write ||
              permissionsAzureBlobStorage.find((perm) => perm === BlobSasPermissions.Write) !== undefined
            }
            copy={
              permissions === DocumentPermissions.Write ||
              permissionsAzureBlobStorage.find((perm) => perm === BlobSasPermissions.Write) !== undefined
            }
            download={true}
          />
          <Upload chunkSize={4_000_000} />
          <Toolbar fileSelectionItems={fileSelectionItemsToolbar} />
          <ContextMenu items={contextMenuItems} />
        </FileManager>
      </>
    )
  } else {
    return <div>Non si dispone dei permessi di accesso necessari. Contattare un amministratore per informazioni</div>
  }
}

export default AttachmentsManager
